// Markdown editor for draft bodies. CodeMirror 6 with the structural
// niceties (history, bracket match, line wrap) but no language
// highlighter — the live preview pane is where formatting shows. Themed
// to BKA's warm-publication register with a serif content face so
// drafting feels like writing on paper (08_AUTHORING_APP.md).
//
// Adapted from agicore-foundry's DocCodeMirror.tsx (dark/mono → light/
// serif; agi language support dropped).

import { useEffect, useRef } from "react";
import { Compartment, EditorState } from "@codemirror/state";
import { EditorView, keymap, highlightActiveLine, drawSelection } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { bracketMatching, indentOnInput } from "@codemirror/language";
import { closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete";

const docTheme = EditorView.theme(
  {
    "&": {
      height: "100%",
      backgroundColor: "var(--paper)",
      color: "var(--ink)",
      fontSize: "16px",
    },
    ".cm-scroller": {
      fontFamily: "var(--font-serif)",
      lineHeight: "1.7",
      padding: "8px 4px",
    },
    ".cm-content": { caretColor: "var(--teal)", maxWidth: "680px" },
    ".cm-cursor": { borderLeftColor: "var(--teal)" },
    ".cm-activeLine": { backgroundColor: "rgba(13,110,110,0.05)" },
    ".cm-selectionBackground, ::selection": { backgroundColor: "rgba(13,110,110,0.18) !important" },
  },
  { dark: false },
);

export function DocCodeMirror({
  value,
  onChange,
  docId,
  readOnly = false,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Resets the editor (and its undo history) when the draft changes. */
  docId: string;
  readOnly?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const readOnlyCompartment = useRef(new Compartment());
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Mount once per docId — switching drafts gives a fresh editor + history.
  useEffect(() => {
    if (!hostRef.current) return;
    const state = EditorState.create({
      doc: value,
      extensions: [
        highlightActiveLine(),
        drawSelection(),
        history(),
        indentOnInput(),
        bracketMatching(),
        closeBrackets(),
        EditorView.lineWrapping,
        keymap.of([...defaultKeymap, ...historyKeymap, ...closeBracketsKeymap]),
        readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly)),
        docTheme,
        EditorView.updateListener.of((u) => {
          if (u.docChanged) onChangeRef.current(u.state.doc.toString());
        }),
      ],
    });
    const view = new EditorView({ state, parent: hostRef.current });
    viewRef.current = view;
    return () => { view.destroy(); viewRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // readOnly toggles live (AI editing mid-stream) without remount.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({ effects: readOnlyCompartment.current.reconfigure(EditorState.readOnly.of(readOnly)) });
  }, [readOnly]);

  // External value updates (AI <edit> landed, or draft reloaded). Only
  // push when content actually differs — avoids cursor jumps while typing.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === value) return;
    view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: value } });
  }, [value]);

  return <div ref={hostRef} className="doc-cm-host" style={{ height: "100%", overflow: "auto" }} />;
}
